import { Link, useParams } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import StatusBadge from "@/components/base/StatusBadge";
import useSeo from "@/hooks/useSeo";
import { scoops } from "@/mocks/scoops";
import RelatedScoops from "@/pages/scoop-detail/components/RelatedScoops";
import ScoopSidebar from "@/pages/scoop-detail/components/ScoopSidebar";
import ScoopBody from "@/pages/scoop-detail/components/ScoopBody";

const ORIGIN = "https://jyhtdd.cn";

export default function ScoopDetailPage() {
  const { id } = useParams();
  const scoop = scoops.find((s) => s.id === id);

  useSeo({
    title: scoop
      ? `${scoop.title} - 看料爆料`
      : "爆料详情 - 看料爆料",
    description: scoop ? scoop.excerpt.slice(0, 120) : "查看看料爆料线索详情、核实状态与相关信息。",
    keywords: scoop ? `${scoop.tags.join(",")},看料爆料,爆料线索,匿名投稿` : "看料爆料,爆料详情",
    canonical: `${ORIGIN}/scoops/${id ?? ""}`,
  });

  if (!scoop) {
    return (
      <section className="mx-auto w-full max-w-shell px-4 py-24 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条爆料不存在或已下线</h1>
        <p className="mt-3 text-sm text-foreground-600">
          可能是链接有误，也可能是内容在核实后被更正或撤回。你可以回到爆料库继续浏览。
        </p>
        <Link
          to="/scoops"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true"></i>
          返回爆料库
        </Link>
      </section>
    );
  }

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: scoop.title,
      description: scoop.excerpt,
      datePublished: scoop.date,
      articleSection: scoop.categoryLabel,
      inLanguage: "zh-CN",
      keywords: scoop.tags.join(","),
      author: { "@type": "Organization", name: "看料爆料" },
      publisher: { "@type": "Organization", name: "看料爆料" },
      mainEntityOfPage: `${ORIGIN}/scoops/${scoop.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "爆料库", item: `${ORIGIN}/scoops` },
        { "@type": "ListItem", position: 3, name: scoop.title, item: `${ORIGIN}/scoops/${scoop.id}` },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={jsonLd} />
      <article>
        <header className="border-b border-foreground-950/10 bg-background-100 paper-grain">
          <div className="mx-auto w-full max-w-shell px-4 py-10 md:px-6 md:py-14">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "爆料库", to: "/scoops" },
                { label: scoop.categoryLabel, to: `/scoops?cat=${scoop.category}` },
                { label: "正文" },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Link
                to={`/scoops?cat=${scoop.category}`}
                className="rounded-full bg-primary-600 px-3 py-0.5 text-[11px] font-medium text-background-50"
              >
                {scoop.categoryLabel}
              </Link>
              <StatusBadge status={scoop.status} />
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-snug tracking-tight text-foreground-950 md:text-4xl">
              {scoop.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {scoop.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" aria-hidden="true"></i>
                {scoop.reads.toLocaleString("zh-CN")} 次阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line" aria-hidden="true"></i>
                {scoop.location}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-voice-line" aria-hidden="true"></i>
                {scoop.source}
              </span>
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-shell px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="h-64 w-full overflow-hidden rounded-lg md:h-80">
                <img
                  src={scoop.cover}
                  alt={`${scoop.title} - 看料爆料线索配图`}
                  title={`${scoop.title} - 看料爆料`}
                  className="h-full w-full object-cover"
                />
              </div>
              <ScoopBody content={scoop.content} />
            </div>
            <div className="lg:col-span-4">
              <ScoopSidebar scoop={scoop} />
            </div>
          </div>
        </div>
      </article>

      <RelatedScoops currentId={scoop.id} category={scoop.category} />
    </>
  );
}